//! TEMPLATE: 顶栏组件(回退按钮 + 面包屑 + 帮助信息按钮)
//! 严格基于 docs/ui_topbar/templates/01-PageBreadcrumb.tsx.template,
//! 仅将 CUSTOMIZE 部分替换为 gitman 的路由与文案。
import { useMemo, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  makeStyles,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  Button,
  Tooltip,
  InfoLabel,
  Text,
  tokens,
} from '@fluentui/react-components'
import { ArrowLeftRegular, HomeRegular } from '@fluentui/react-icons'
import { ROUTES } from '@/routes'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '100%',
    minWidth: 0,
  },
  backButton: { flexShrink: 0 },
  breadcrumbWrap: { minWidth: 0, overflow: 'hidden', flex: 1 },
  // 模板类 helpButton 仅 flexShrink:0; InfoLabel 根是 inline-block Label,
  // info 图标按文本基线对齐导致视觉偏下。显式改为 flex 容器居中(不改模板语义)。
  helpButton: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    lineHeight: 1,
  },
  helpPopover: {
    maxWidth: '420px',
    padding: '12px 14px',
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: '1.5',
  },
})

interface CrumbItem {
  label: ReactNode
  key: string
  route?: string
}

// ===== 路由 → 帮助文案映射 =====
const ROUTE_HELP: Record<string, ReactNode> = {
  [ROUTES.home]: '系统概览。展示用户、仓库、分支、备份等全局统计指标。',
  [ROUTES.explore]: '浏览公开仓库,可按名称或描述模糊搜索。',
  [ROUTES.dashboard]: '我的仓库。管理你创建或参与的仓库。',
  [ROUTES.settings]: '个人设置。修改昵称、邮箱与头像。',
  [ROUTES.adminUsers]: '用户管理。查看、创建、启停用户,点击行进入详情。',
  [ROUTES.adminRepos]: '仓库管理。全局查看与维护所有仓库。',
  [ROUTES.adminBackups]: '备份管理。创建、下载、恢复仓库备份。',
  [ROUTES.adminSettings]: '系统设置。配置站点信息、鉴权方式与 OAuth 登录。',
}

/**
 * 通用顶栏组件:左侧"返回上一级"按钮 + 中间面包屑路径 + 帮助信息按钮。
 * 由 Layout 统一渲染,各页面无需自带回退按钮。
 */
export function PageBreadcrumb() {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()

  const crumbs = useMemo<CrumbItem[]>(() => {
    const p = location.pathname

    // 首页 - 只有一级, 回退按钮禁用
    if (p === ROUTES.home) {
      return [{ label: '系统概览', key: 'home' }]
    }

    // 其他顶层页面
    const routeTitles: Record<string, string> = {
      [ROUTES.explore]: '浏览',
      [ROUTES.dashboard]: '我的仓库',
      [ROUTES.settings]: '个人设置',
      [ROUTES.adminUsers]: '用户管理',
      [ROUTES.adminRepos]: '仓库管理',
      [ROUTES.adminBackups]: '备份管理',
      [ROUTES.adminSettings]: '系统设置',
    }

    return [
      { label: <HomeRegular />, key: 'home', route: ROUTES.home },
      { label: routeTitles[p] ?? '默认标题', key: 'title' },
    ]
  }, [location.pathname])

  // 帮助文案:精确匹配;详情页继承父级
  const helpInfo = useMemo(() => {
    const p = location.pathname
    if (ROUTE_HELP[p]) return ROUTE_HELP[p]
    return null
  }, [location.pathname])

  // 回退:跳到倒数第二级; 只有一级时禁用
  const canGoBack = crumbs.length >= 2
  const onBack = () => {
    if (!canGoBack) return
    const target = crumbs[crumbs.length - 2]
    if (target?.route) {
      navigate(target.route)
    } else {
      navigate(ROUTES.home)
    }
  }

  return (
    <div className={styles.root}>
      <Tooltip content={canGoBack ? '返回' : '已在首页'} relationship="label">
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowLeftRegular />}
          className={styles.backButton}
          onClick={onBack}
          disabled={!canGoBack}
          aria-label="返回上一级"
        />
      </Tooltip>
      <div className={styles.breadcrumbWrap}>
        <Breadcrumb size="small">
          {crumbs.map((item, i) => (
            <BreadcrumbItem key={item.key}>
              {i > 0 && <BreadcrumbDivider />}
              <BreadcrumbButton
                onClick={() => {
                  if (item.route) navigate(item.route)
                }}
              >
                {item.label}
              </BreadcrumbButton>
            </BreadcrumbItem>
          ))}
        </Breadcrumb>
      </div>
      {helpInfo && (
        <InfoLabel
          className={styles.helpButton}
          info={<div className={styles.helpPopover}>{helpInfo}</div>}
          size="small"
        />
      )}
    </div>
  )
}

/** 页面标题组件(独立导出,页面内容区 H1) */
export function PageTitle({ children, subtitle }: { children: ReactNode; subtitle?: string }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <Text as="h1" size={500} weight="semibold" block>{children}</Text>
      {subtitle && (
        <Text size={300} style={{ color: 'var(--colorNeutralForeground3)' }} block>
          {subtitle}
        </Text>
      )}
    </div>
  )
}